<template>
    <Teleport to="body">
        <div v-if="!isCompact" :class="['dock-root', {'dock-root--right': isRight}]">
            <div v-if="dockState.autoHide" :class="['app-dock-trigger', {'app-dock-trigger--right': isRight}]"
                 @mouseenter="showDock" @touchstart="showDock"></div>

            <div ref="dock" :class="['app-dock', {'app-dock--hidden': dockState.autoHide && !dockVisible, 'app-dock--right': isRight, 'app-dock--drag-outside': dragState && dragState.outsideDock}]"
                 :style="{'--dock-icon-size': dockState.iconSize + 'px'}"
                 @mouseenter="onDockEnter" @mouseleave="onDockLeave"
                 @mousemove="onMouseMove"
                 @touchstart="onDockTouchStart" @touchend="onDockTouchEnd"
                 @pointerdown="onDockPointerDown"
                 @contextmenu.prevent="openDockContextMenu($event)">
                <div class="app-dock-bg"></div>
                <div class="app-dock-items">
                    <DockItem v-for="(item, idx) in dockState.pageItems" :key="item.id"
                              ref="items"
                              data-dock-section="page"
                              :icon="item.icon || getIcon(item.id)"
                              :label="$t(item.labelKey)"
                              :color="item.color"
                              :active="isPageActive(item)"
                              :is-draggable="!dragLocked"
                              :dragging="!!dragState && dragState.section === 'page' && dragState.fromIndex === idx"
                              :item-index="idx"
                              :drag-shift="getDragShift('page', idx)"
                              :click-animation-enabled="dockState.clickAnimation"
                              section="page"
                              @click="navigateTo(item)"
                              @contextmenu="onItemContextMenu($event, item, 'page')"
                              @pointer-down="onItemPointerDown"/>

                    <DockSeparator ref="sep1" @resize="onSeparatorResize"/>

                    <DockItem v-for="(item, idx) in dockState.panelItems" :key="item.id"
                              ref="items"
                              data-dock-section="panel"
                              :data-dock-panel="item.panelId"
                              :icon="item.icon || getIcon(item.id)"
                              :label="$t(item.labelKey)"
                              :color="(item.panelId === 'weatherPanel' || item.panelId === 'agendaPanel' || item.panelId === 'fileManagerPanel' || item.panelId === 'musicPanel') ? '' : item.color"
                              :active="isPanelActive(item.panelId)"
                              :minimized="isPanelMinimized(item.panelId)"
                              :is-draggable="!dragLocked"
                              :dragging="!!dragState && dragState.section === 'panel' && dragState.fromIndex === idx"
                              :item-index="idx"
                              :drag-shift="getDragShift('panel', idx)"
                              :click-animation-enabled="dockState.clickAnimation"
                              section="panel"
                              @click="onPanelItemClick(item.panelId)"
                              @contextmenu="onItemContextMenu($event, item, 'panel')"
                              @pointer-down="onItemPointerDown">
                        <DockWeatherIcon v-if="item.panelId === 'weatherPanel'"/>
                        <DockCalendarIcon v-else-if="item.panelId === 'agendaPanel'"/>
                        <DockFileManagerIcon v-else-if="item.panelId === 'fileManagerPanel'"/>
                        <DockMusicIcon v-else-if="item.panelId === 'musicPanel'"/>
                    </DockItem>
                </div>
            </div>

            <div v-if="contextMenu.visible && contextMenu.type === 'dock'" class="dock-context-menu"
                 :style="contextMenuStyle">
                <button class="dock-context-item" @click="onToggleAutoHide">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M13 17h5l3-3-3-3h-5"/><path d="M11 7H6L3 10l3 3h5"/><path d="M12 2v6"/><path d="M12 16v6"/></svg>
                    {{ $t('common.dock.autoHide') }}
                    <span class="dock-context-toggle" :class="{'dock-context-toggle--on': dockState.autoHide}"></span>
                </button>
                <div class="dock-context-divider"></div>
                <button class="dock-context-item" @click="openCustomizePanel">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    {{ $t('common.dock.customize') }}
                </button>
                <div class="dock-context-divider"></div>
                <button class="dock-context-item" @click="onResetOrder">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h6l2 2h7a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="m14 13-2 2 2 2"/><path d="M16 15h-4"/></svg>
                    {{ $t('common.dock.resetOrder') }}
                </button>
                <button class="dock-context-item" @click="onResetCustomizations">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>
                    {{ $t('common.dock.resetCustomizations') }}
                </button>
            </div>

            <div v-if="contextMenu.visible && contextMenu.type === 'item'" class="dock-context-menu"
                 :style="contextMenuStyle">
                <button v-if="contextMenu.targetSection === 'panel' && isPanelActive(contextMenu.targetItem.panelId)"
                        class="dock-context-item" @click="onClosePanel">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    {{ $t('common.dock.close') }}
                </button>
                <button v-if="contextMenu.targetItem?.panelId === 'weatherPanel'"
                        class="dock-context-item" @click="onToggleWeatherAnimation">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v4"/><path d="m16.24 7.76 2.83-2.83"/><path d="M22 12h-4"/><path d="m16.24 16.24 2.83 2.83"/><path d="M12 22v-4"/><path d="m4.93 19.07 2.83-2.83"/><path d="M2 12h4"/><path d="m4.93 4.93 2.83 2.83"/></svg>
                    {{ $t('common.dock.weatherAnimation') }}
                    <span class="dock-context-toggle" :class="{'dock-context-toggle--on': weatherDockAnimation}"></span>
                </button>
                <button v-if="contextMenu.targetItem?.panelId === 'musicPanel'"
                        class="dock-context-item" @click="onToggleMusicAnimation">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                    {{ $t('common.dock.musicAnimation') }}
                    <span class="dock-context-toggle" :class="{'dock-context-toggle--on': musicDockAnimation}"></span>
                </button>
                <button v-if="contextMenu.targetItem?.panelId === 'musicPanel' && musicIsPlaying"
                        class="dock-context-item" @click="onMusicShutdown">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                    {{ $t('common.dock.musicStop') }}
                </button>
                <button v-if="contextMenu.targetItem?.panelId === 'fileManagerPanel'"
                        class="dock-context-item" @click="onToggleFmBadge">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                    {{ $t('common.notificationCount') }}
                    <span class="dock-context-toggle" :class="{'dock-context-toggle--on': fmDockBadge}"></span>
                </button>
                <button v-if="contextMenu.targetItem?.panelId === 'agendaPanel'"
                        class="dock-context-item" @click="onToggleAgendaBadge">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                    {{ $t('common.notificationCount') }}
                    <span class="dock-context-toggle" :class="{'dock-context-toggle--on': agendaDockBadge}"></span>
                </button>
                <button class="dock-context-item dock-context-item--danger" @click="onRemoveFromDock">
                    <svg class="dock-context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>
                    {{ $t('common.dock.removeFromDock') }}
                </button>
            </div>

            <div v-if="contextMenu.visible" style="position:fixed;inset:0;z-index:100399"
                 @click="closeContextMenu" @contextmenu.prevent="closeContextMenu"></div>

            <div v-if="customizeOpen" class="dock-customize-panel"
                 :style="customizePanelStyle">
                <div class="dock-customize-section">
                    <div class="dock-customize-title">{{ $t('common.dock.pages') }}</div>
                    <label v-for="item in dockState.allPageItems" :key="item.id" class="dock-customize-row">
                        <span class="dock-customize-icon" v-html="item.icon || getIcon(item.id)"
                              :style="{background: item.color}"></span>
                        <span class="dock-customize-label">{{ $t(item.labelKey) }}</span>
                        <span class="dock-context-toggle" :class="{'dock-context-toggle--on': isItemInDock('page', item.id)}"
                              @click.prevent="toggleDockItem('page', item.id)"></span>
                    </label>
                </div>
                <div class="dock-customize-divider"></div>
                <div class="dock-customize-section">
                    <div class="dock-customize-title">{{ $t('common.dock.panels') }}</div>
                    <label v-for="item in dockState.allPanelItems" :key="item.id" class="dock-customize-row">
                        <span class="dock-customize-icon" v-html="item.icon || getIcon(item.id)"
                              :style="{background: item.color}"></span>
                        <span class="dock-customize-label">{{ $t(item.labelKey) }}</span>
                        <span class="dock-context-toggle" :class="{'dock-context-toggle--on': isItemInDock('panel', item.id)}"
                              @click.prevent="toggleDockItem('panel', item.id)"></span>
                    </label>
                </div>
                <div class="dock-customize-divider"></div>
                <div class="dock-customize-section">
                    <div class="dock-customize-title">{{ $t('common.dock.settings') }}</div>
                    <div class="dock-customize-row" @click="onToggleMagnification">
                        <span class="dock-customize-label">{{ $t('common.dock.magnification') }}</span>
                        <span class="dock-context-toggle" :class="{'dock-context-toggle--on': dockState.magnification}"></span>
                    </div>
                    <div v-if="dockState.magnification" class="dock-context-scale" style="padding-left:8px">
                        <span class="dock-context-scale-label">{{ $t('common.dock.magnificationScale') }}</span>
                        <button class="dock-context-scale-btn" @click="onScaleDown">−</button>
                        <span class="dock-context-scale-value">{{ dockState.magnificationScale.toFixed(1) }}x</span>
                        <button class="dock-context-scale-btn" @click="onScaleUp">+</button>
                    </div>
                    <div class="dock-customize-row" @click="onToggleClickAnimation">
                        <span class="dock-customize-label">{{ $t('common.dock.clickAnimation') }}</span>
                        <span class="dock-context-toggle" :class="{'dock-context-toggle--on': dockState.clickAnimation}"></span>
                    </div>
                    <div class="dock-customize-row" @click="onToggleBounceAnimation">
                        <span class="dock-customize-label">{{ $t('common.dock.bounceAnimation') }}</span>
                        <span class="dock-context-toggle" :class="{'dock-context-toggle--on': dockState.bounceAnimation}"></span>
                    </div>
                    <div v-if="dockState.bounceAnimation" class="dock-customize-row" style="padding-left:8px" @click="onToggleBounceOnOpen">
                        <span class="dock-customize-label">{{ $t('common.dock.bounceOnOpen') }}</span>
                        <span class="dock-context-toggle" :class="{'dock-context-toggle--on': dockState.bounceOnOpen}"></span>
                    </div>
                    <div class="dock-context-position" style="padding-left:8px">
                        <span class="dock-context-position-label">{{ $t('common.dock.position') }}</span>
                        <button :class="['dock-context-position-btn', {'dock-context-position-btn--active': dockState.position === 'bottom'}]"
                                @click="onSetPosition('bottom')">{{ $t('common.dock.positionBottom') }}</button>
                        <button :class="['dock-context-position-btn', {'dock-context-position-btn--active': dockState.position === 'right'}]"
                                @click="onSetPosition('right')">{{ $t('common.dock.positionRight') }}</button>
                    </div>
                </div>
            </div>
            <div v-if="customizeOpen" style="position:fixed;inset:0;z-index:100399"
                 @click="customizeOpen = false" @contextmenu.prevent="customizeOpen = false"></div>
        </div>
    </Teleport>
</template>

<script>
import './dock.css'
import DockItem from './DockItem.vue'
import DockSeparator from './DockSeparator.vue'
import DockWeatherIcon from '@/components/weather/DockWeatherIcon.vue'
import DockCalendarIcon from '@/components/agenda/DockCalendarIcon.vue'
import DockFileManagerIcon from '@/components/filemanager/DockFileManagerIcon.vue'
import DockMusicIcon from '@/components/music/DockMusicIcon.vue'
import musicService from '@/components/music/musicService'
import weatherService from '@/components/weather/weatherService'
import fileManagerService from '@/components/filemanager/fileManagerService'
import agendaBadgeService from '@/components/agenda/agendaBadgeService'
import {useDockState} from './useDockState'
import panelManager from '@/core/helpers/panelManager'

export default {
    name: 'AppDock',
    components: {DockItem, DockSeparator, DockWeatherIcon, DockCalendarIcon, DockFileManagerIcon, DockMusicIcon},
    setup() {
        const dockApi = useDockState()
        return {
            dockState: dockApi.state,
            toggleAutoHide: dockApi.toggleAutoHide,
            toggleMagnification: dockApi.toggleMagnification,
            setMagnificationScale: dockApi.setMagnificationScale,
            setIconSize: dockApi.setIconSize,
            setPosition: dockApi.setPosition,
            toggleClickAnimation: dockApi.toggleClickAnimation,
            toggleBounceAnimation: dockApi.toggleBounceAnimation,
            toggleBounceOnOpen: dockApi.toggleBounceOnOpen,
            isPanelActive: dockApi.isPanelActive,
            isPanelMinimized: dockApi.isPanelMinimized,
            _dockPanelClick: dockApi.onPanelItemClick,
            reorderItem: dockApi.reorderItem,
            removeItem: dockApi.removeItem,
            addItem: dockApi.addItem,
            isItemInDock: dockApi.isItemInDock,
            getIcon: dockApi.getIcon,
            closePanelFn: dockApi.closePanel,
            resetItemOrder: dockApi.resetItemOrder,
            resetCustomizations: dockApi.resetCustomizations,
        }
    },
    data() {
        return {
            windowWidth: window.innerWidth,
            mouseX: 0,
            mouseY: 0,
            mouseActive: false,
            dockVisible: false,
            hideTimer: null,
            contextMenu: {visible: false, type: 'dock', x: 0, y: 0, targetItem: null, targetSection: null},
            customizeOpen: false,
            dragState: null,
            dragLocked: false,
        }
    },
    computed: {
        isCompact() {
            return this.windowWidth < 700
        },
        isRight() {
            return this.dockState.position === 'right'
        },
        weatherDockAnimation() {
            return weatherService.state.dockAnimation
        },
        musicDockAnimation() {
            return musicService.state.dockAnimation
        },
        musicIsPlaying() {
            return musicService.state.playing || musicService.state.buffering
        },
        fmDockBadge() {
            return fileManagerService.state.dockBadge
        },
        fmUploadingCount() {
            return fileManagerService.state.uploadQueue.filter(u => u.status === 'uploading').length
        },
        agendaDockBadge() {
            return agendaBadgeService.state.dockBadge
        },
        contextMenuStyle() {
            if (!this.contextMenu.visible) return {}
            if (this.isRight) {
                return {
                    right: this.contextMenu.x + 'px',
                    top: this.contextMenu.y + 'px',
                }
            }
            return {
                left: this.contextMenu.x + 'px',
                bottom: this.contextMenu.y + 'px',
            }
        },
        customizePanelStyle() {
            const dock = this.$refs.dock
            if (!dock) return {bottom: '80px', left: '50%', transform: 'translateX(-50%)'}
            const rect = dock.getBoundingClientRect()
            if (this.isRight) {
                return {
                    right: (window.innerWidth - rect.left + 12) + 'px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                }
            }
            return {
                bottom: (window.innerHeight - rect.top + 12) + 'px',
                left: '50%',
                transform: 'translateX(-50%)',
            }
        },
    },
    watch: {
        fmUploadingCount(newVal, oldVal) {
            if (this.dockState.bounceAnimation && oldVal > 0 && newVal === 0 && (!this.isPanelActive('fileManagerPanel') || this.isPanelMinimized('fileManagerPanel'))) {
                this.$nextTick(() => {
                    this.triggerPanelBounce('fileManagerPanel', 'attention')
                    setTimeout(() => this.triggerPanelBounce('fileManagerPanel', 'attention'), 1800)
                    setTimeout(() => this.triggerPanelBounce('fileManagerPanel', 'attention'), 3600)
                })
            }
        },
    },
    methods: {
        isPageActive(item) {
            return this.$route?.path?.startsWith(item.route)
        },
        navigateTo(item) {
            if (this.$router) this.$router.push(item.route)
        },
        onPanelItemClick(panelId) {
            const inst = panelManager.getPanelInstance(panelId)
            if (!inst) return
            const shouldBounce = this.dockState.bounceAnimation && this.dockState.bounceOnOpen && !inst.visible
            this._dockPanelClick(panelId)
            if (shouldBounce) {
                this.$nextTick(() => this.triggerPanelBounce(panelId, 'open'))
            }
        },
        triggerPanelBounce(panelId, type) {
            const items = this.$refs.items || []
            const item = items.find(i => i.$el?.dataset?.dockPanel === panelId)
            if (item) item.triggerBounce(type)
        },

        onDockEnter() {
            if (this._resizing || this.dragState || this._isTouchInteraction) return
            clearTimeout(this.hideTimer)
            this.dockVisible = true
            if (!this.dockState.magnification) return
            this.mouseActive = true
            const dock = this.$refs.dock
            if (dock) {
                const prop = this.isRight ? 'width' : 'height'
                const rect = dock.getBoundingClientRect()
                dock.style[prop] = rect[prop] + 'px'
                dock.classList.add('app-dock--magnifying')
            }
            this.updateMagnification()
        },
        onDockLeave() {
            if (this._resizing || this.dragState) return
            this.mouseActive = false
            this.resetMagnification()
            setTimeout(() => {
                if (!this.mouseActive) {
                    const dock = this.$refs.dock
                    if (dock) {
                        dock.classList.remove('app-dock--magnifying')
                        dock.style.height = ''
                        dock.style.width = ''
                    }
                }
            }, 150)
            if (this.dockState.autoHide) {
                this.hideTimer = setTimeout(() => {
                    this.dockVisible = false
                }, 400)
            }
        },
        onMouseMove(e) {
            this.mouseX = e.clientX
            this.mouseY = e.clientY
        },
        showDock() {
            clearTimeout(this.hideTimer)
            this.dockVisible = true
        },
        onDockTouchStart() {
            this._isTouchInteraction = true
        },
        onDockTouchEnd() {
            setTimeout(() => { this._isTouchInteraction = false }, 500)
        },

        updateMagnification() {
            if (!this.mouseActive || !this.$refs.dock || this.dragState) return
            const maxDist = 140
            const maxScale = this.dockState.magnificationScale
            const maxLift = 8 + (maxScale - 1) * 16
            const baseSize = this.dockState.iconSize
            const right = this.isRight
            const mousePos = right ? this.mouseY : this.mouseX
            const items = this.$refs.items
            if (items) {
                const arr = Array.isArray(items) ? items : [items]
                const data = []
                for (let i = 0; i < arr.length; i++) {
                    const el = arr[i]?.$el
                    if (!el) { data.push(null); continue }
                    const iconEl = el.querySelector('.dock-item-icon')
                    if (!iconEl) { data.push(null); continue }
                    const rect = el.getBoundingClientRect()
                    const center = right ? (rect.top + rect.height / 2) : (rect.left + rect.width / 2)
                    data.push({el, iconEl, center})
                }
                for (let i = 0; i < data.length; i++) {
                    const d = data[i]
                    if (!d) continue
                    const dist = Math.abs(mousePos - d.center)
                    const t = dist >= maxDist ? 0 : (1 + Math.cos(Math.PI * dist / maxDist)) / 2
                    const size = baseSize + (baseSize * (maxScale - 1)) * t
                    const lift = maxLift * t
                    d.iconEl.style.width = size.toFixed(1) + 'px'
                    d.iconEl.style.height = size.toFixed(1) + 'px'
                    d.iconEl.style.setProperty('--dock-icon-size', size.toFixed(1) + 'px')
                    if (right) {
                        d.el.style.transform = lift > 0.5 ? 'translateX(-' + lift.toFixed(1) + 'px)' : ''
                    } else {
                        d.el.style.transform = lift > 0.5 ? 'translateY(-' + lift.toFixed(1) + 'px)' : ''
                    }
                }
            }
            if (this.mouseActive) requestAnimationFrame(() => this.updateMagnification())
        },
        resetMagnification() {
            const items = this.$refs.items
            if (!items) return
            const arr = Array.isArray(items) ? items : [items]
            arr.forEach(comp => {
                const el = comp?.$el
                if (!el) return
                const iconEl = el.querySelector('.dock-item-icon')
                if (!iconEl) return
                iconEl.style.width = ''
                iconEl.style.height = ''
                iconEl.style.removeProperty('--dock-icon-size')
                el.style.transform = ''
            })
        },

        onSeparatorResize({phase, dy, dx}) {
            const dock = this.$refs.dock
            if (phase === 'start') {
                this._resizing = true
                this._resizeStartSize = this.dockState.iconSize
                this.mouseActive = false
                this.resetMagnification()
                if (dock) {
                    dock.classList.remove('app-dock--magnifying')
                    dock.style.height = ''
                    dock.style.width = ''
                    dock.classList.add('app-dock--resizing')
                }
            } else if (phase === 'move') {
                const delta = this.isRight ? dx : dy
                this.setIconSize(this._resizeStartSize + delta)
            } else if (phase === 'end') {
                this._resizing = false
                dock?.classList.remove('app-dock--resizing')
            }
        },

        onDockPointerDown(e) {
            if (e.pointerType !== 'touch' || e.button !== 0) return
            if (e.target.closest('.dock-item')) return
            const startX = e.clientX
            const startY = e.clientY
            const timer = setTimeout(() => {
                cleanup()
                this.openDockContextMenu(e)
            }, 700)
            const onMove = (ev) => {
                if (Math.abs(ev.clientX - startX) > 10 || Math.abs(ev.clientY - startY) > 10) cleanup()
            }
            const onUp = () => { cleanup() }
            const cleanup = () => {
                clearTimeout(timer)
                document.removeEventListener('pointermove', onMove)
                document.removeEventListener('pointerup', onUp)
            }
            document.addEventListener('pointermove', onMove)
            document.addEventListener('pointerup', onUp)
        },

        getDragShift(section, index) {
            if (!this.dragState || this.dragState.section !== section) return ''
            const {fromIndex, currentIndex} = this.dragState
            if (index === fromIndex) return ''
            if (fromIndex < currentIndex) {
                if (index > fromIndex && index <= currentIndex) return 'left'
            } else if (fromIndex > currentIndex) {
                if (index >= currentIndex && index < fromIndex) return 'right'
            }
            return ''
        },

        onItemPointerDown({index, section, event}) {
            const startX = event.clientX
            const startY = event.clientY
            let started = false
            let ghost = null
            let itemRects = null
            let longPressTimer = null
            const isTouch = event.pointerType === 'touch'
            const items = section === 'page' ? this.dockState.pageItems : this.dockState.panelItems
            const item = items[index]

            if (isTouch) {
                longPressTimer = setTimeout(() => {
                    longPressTimer = null
                    document.removeEventListener('pointermove', onMove)
                    document.removeEventListener('pointerup', onUp)
                    this._suppressClickOnItem(section, index)
                    const item = section === 'page' ? this.dockState.pageItems[index] : this.dockState.panelItems[index]
                    if (item) this.onItemContextMenu(event, item, section)
                }, 700)
            }

            const preventScroll = (e) => { e.preventDefault() }

            const onMove = (e) => {
                const dx = Math.abs(e.clientX - startX)
                const dy = Math.abs(e.clientY - startY)

                if (!started) {
                    if (dx < 5 && dy < 5) return
                    if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null }
                    started = true

                    document.addEventListener('touchmove', preventScroll, {passive: false})

                    this.mouseActive = false
                    this.resetMagnification()
                    const dock = this.$refs.dock
                    if (dock) {
                        dock.classList.remove('app-dock--magnifying')
                        dock.style.height = ''
                        dock.style.width = ''
                    }

                    itemRects = this._snapshotItemRects(section)

                    this.dragState = {section, fromIndex: index, currentIndex: index, outsideDock: false}

                    ghost = this._createGhost(event)
                    if (ghost) {
                        ghost.style.overflow = 'visible'
                        const label = document.createElement('div')
                        label.textContent = item ? this.$t(item.labelKey) : ''
                        label.style.cssText =
                            'position:absolute;bottom:-22px;left:50%;transform:translateX(-50%);' +
                            'font-size:12px;font-weight:500;white-space:nowrap;pointer-events:none;' +
                            'color:var(--dock-tooltip-color);background:var(--dock-tooltip-bg);' +
                            'padding:3px 10px;border-radius:5px;opacity:0;transition:opacity 0.15s ease;' +
                            'font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Segoe UI",sans-serif;'
                        ghost.appendChild(label)
                        ghost._label = label
                    }

                    this._suppressClickOnItem(section, index)
                }

                if (ghost) {
                    const dockEl = this.$refs.dock
                    const dockRect = dockEl ? dockEl.getBoundingClientRect() : null
                    let outsideDock = false
                    if (dockRect) {
                        if (this.isRight) {
                            outsideDock = e.clientX < dockRect.left - 140
                        } else {
                            outsideDock = e.clientY < dockRect.top - 140
                        }
                    }

                    if (outsideDock) {
                        ghost.style.transform = 'translate(' + e.clientX + 'px,' + e.clientY + 'px) translate(-50%,-50%) scale(1.35)'
                        ghost.style.opacity = '0.5'
                        if (ghost._label) ghost._label.style.opacity = '1'
                        if (this.dragState) {
                            this.dragState = {...this.dragState, outsideDock: true, currentIndex: this.dragState.fromIndex}
                        }
                    } else {
                        ghost.style.transform = 'translate(' + e.clientX + 'px,' + e.clientY + 'px) translate(-50%,-50%) scale(1.12)'
                        ghost.style.opacity = '0.92'
                        if (ghost._label) ghost._label.style.opacity = '0'
                        if (this.dragState && this.dragState.outsideDock) {
                            this.dragState = {...this.dragState, outsideDock: false}
                        }
                    }
                }

                if (!this.dragState?.outsideDock) {
                    const clientPos = this.isRight ? e.clientY : e.clientX
                    const newIdx = this._calcDropIndex(clientPos, itemRects)
                    if (newIdx !== -1 && this.dragState && this.dragState.currentIndex !== newIdx) {
                        this.dragState = {...this.dragState, currentIndex: newIdx}
                    }
                }
            }

            const onUp = (e) => {
                if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null }
                document.removeEventListener('pointermove', onMove)
                document.removeEventListener('pointerup', onUp)
                document.removeEventListener('touchmove', preventScroll)

                if (started && this.dragState?.outsideDock && ghost) {
                    const g = ghost
                    ghost = null
                    this._playPoofEffect(g, e.clientX, e.clientY)
                    this.removeItem(this.dragState.section, item.id)
                    this.dragState = null
                    return
                }

                if (ghost) { ghost.remove(); ghost = null }

                if (started && this.dragState) {
                    const {section: s, fromIndex: from, currentIndex: to} = this.dragState
                    const dock = this.$refs.dock
                    if (from !== to) {
                        if (dock) dock.classList.add('app-dock--no-transition')
                        this.dragState = null
                        this.reorderItem(s, from, to)
                        this.$nextTick(() => {
                            if (dock) { dock.offsetHeight; dock.classList.remove('app-dock--no-transition') }
                        })
                    } else {
                        this.dragState = null
                    }
                }
            }

            document.addEventListener('pointermove', onMove)
            document.addEventListener('pointerup', onUp)
        },

        _snapshotItemRects(section) {
            const container = this.$refs.dock?.querySelector('.app-dock-items')
            if (!container) return []
            const els = container.querySelectorAll('.dock-item[data-dock-section="' + section + '"]')
            const right = this.isRight
            return Array.from(els).map(el => {
                const r = el.getBoundingClientRect()
                return right
                    ? {top: r.top, bottom: r.bottom, center: r.top + r.height / 2}
                    : {left: r.left, right: r.right, center: r.left + r.width / 2}
            })
        },

        _createGhost(event) {
            const itemEl = event.target.closest('.dock-item')
            const iconEl = itemEl?.querySelector('.dock-item-icon')
            if (!iconEl) return null
            const clone = iconEl.cloneNode(true)
            const rect = iconEl.getBoundingClientRect()
            clone.style.cssText =
                'position:fixed;left:0;top:0;pointer-events:none;z-index:200000;' +
                'width:' + rect.width + 'px;height:' + rect.height + 'px;' +
                'border-radius:22.5%;will-change:transform;' +
                'transform:translate(' + event.clientX + 'px,' + event.clientY + 'px) translate(-50%,-50%) scale(1.12);' +
                'opacity:0.92;filter:brightness(1.08);' +
                'box-shadow:0 8px 30px rgba(0,0,0,0.25);' +
                'transition:opacity 0.15s ease;'
            document.body.appendChild(clone)
            return clone
        },

        _playPoofEffect(ghost, cx, cy) {
            if (ghost._label) ghost._label.style.opacity = '0'

            const rect = ghost.getBoundingClientRect()
            const w = rect.width
            const h = rect.height
            const cols = 4
            const rows = 4
            const cellW = w / cols
            const cellH = h / rows

            const container = document.createElement('div')
            container.style.cssText =
                'position:fixed;left:0;top:0;pointer-events:none;z-index:200000;' +
                'width:0;height:0;'
            document.body.appendChild(container)

            const shards = []
            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) {
                    const shard = ghost.cloneNode(true)
                    const clipX = c * cellW
                    const clipY = r * cellH
                    shard.style.cssText =
                        'position:absolute;pointer-events:none;' +
                        'width:' + w + 'px;height:' + h + 'px;' +
                        'left:' + (cx - w / 2) + 'px;top:' + (cy - h / 2) + 'px;' +
                        'clip-path:inset(' + clipY + 'px ' + (w - clipX - cellW) + 'px ' + (h - clipY - cellH) + 'px ' + clipX + 'px);' +
                        'transform:translate(0,0) rotate(0deg) scale(1);opacity:1;' +
                        'transition:transform 0.45s cubic-bezier(.36,.07,.19,.97), opacity 0.4s ease-out;' +
                        'border-radius:0;box-shadow:none;filter:none;overflow:hidden;'
                    if (shard._label) shard._label.remove()
                    container.appendChild(shard)

                    const fromCenterX = (clipX + cellW / 2) - w / 2
                    const fromCenterY = (clipY + cellH / 2) - h / 2
                    const angle = Math.atan2(fromCenterY, fromCenterX)
                    const dist = 40 + Math.random() * 50
                    const tx = Math.cos(angle) * dist + (Math.random() - 0.5) * 20
                    const ty = Math.sin(angle) * dist + (Math.random() - 0.5) * 20 + 15
                    const rot = (Math.random() - 0.5) * 60
                    shards.push({el: shard, tx, ty, rot})
                }
            }

            ghost.style.display = 'none'

            requestAnimationFrame(() => {
                shards.forEach(({el, tx, ty, rot}) => {
                    el.style.transform = 'translate(' + tx + 'px,' + ty + 'px) rotate(' + rot + 'deg) scale(0.3)'
                    el.style.opacity = '0'
                })
            })

            setTimeout(() => {
                ghost.remove()
                container.remove()
            }, 550)
        },

        _calcDropIndex(pos, rects) {
            if (!rects || !rects.length) return -1
            let closest = 0
            let minDist = Infinity
            for (let i = 0; i < rects.length; i++) {
                const r = rects[i]
                if (!r) continue
                const d = Math.abs(pos - r.center)
                if (d < minDist) { minDist = d; closest = i }
            }
            return closest
        },

        _suppressClickOnItem(section, index) {
            const allComps = this.$refs.items
            if (!allComps) return
            const arr = Array.isArray(allComps) ? allComps : [allComps]
            const pageCount = this.dockState.pageItems.length
            const compIdx = section === 'page' ? index : pageCount + index
            const comp = arr[compIdx]
            if (comp && typeof comp.suppressNextClick === 'function') {
                comp.suppressNextClick()
            }
        },

        openDockContextMenu(e) {
            this.customizeOpen = false
            const dockRect = this.$refs.dock?.getBoundingClientRect()
            if (!dockRect) return
            if (this.isRight) {
                this.contextMenu = {
                    visible: true, type: 'dock',
                    x: window.innerWidth - dockRect.left + 12,
                    y: e.clientY,
                    targetItem: null, targetSection: null,
                }
            } else {
                this.contextMenu = {
                    visible: true, type: 'dock',
                    x: e.clientX,
                    y: window.innerHeight - dockRect.top + 12,
                    targetItem: null, targetSection: null,
                }
            }
        },
        onItemContextMenu(nativeEvent, item, section) {
            this.customizeOpen = false
            const dockRect = this.$refs.dock?.getBoundingClientRect()
            if (!dockRect) return
            if (this.isRight) {
                this.contextMenu = {
                    visible: true, type: 'item',
                    x: window.innerWidth - dockRect.left + 12,
                    y: nativeEvent.clientY,
                    targetItem: item, targetSection: section,
                }
            } else {
                this.contextMenu = {
                    visible: true, type: 'item',
                    x: nativeEvent.clientX,
                    y: window.innerHeight - dockRect.top + 12,
                    targetItem: item, targetSection: section,
                }
            }
        },
        closeContextMenu() {
            this.contextMenu.visible = false
        },
        onToggleAutoHide() {
            this.toggleAutoHide()
            this.contextMenu.visible = false
            if (this.dockState.autoHide) {
                this.hideTimer = setTimeout(() => { this.dockVisible = false }, 400)
            }
        },
        onToggleMagnification() {
            this.toggleMagnification()
        },
        onScaleDown() {
            this.setMagnificationScale(this.dockState.magnificationScale - 0.1)
        },
        onScaleUp() {
            this.setMagnificationScale(this.dockState.magnificationScale + 0.1)
        },
        onResetOrder() {
            this.resetItemOrder()
            this.contextMenu.visible = false
        },
        onResetCustomizations() {
            this.resetCustomizations()
            this.contextMenu.visible = false
        },
        onToggleClickAnimation() {
            this.toggleClickAnimation()
        },
        onToggleBounceAnimation() {
            this.toggleBounceAnimation()
        },
        onToggleBounceOnOpen() {
            this.toggleBounceOnOpen()
            if (this.dockState.bounceOnOpen) {
                this.$nextTick(() => {
                    const items = this.$refs.items || []
                    let delay = 0
                    items.forEach(item => {
                        if (item.$el?.dataset?.dockPanel) {
                            setTimeout(() => item.triggerBounce('open'), delay)
                            delay += 100
                        }
                    })
                })
            }
        },
        onSetPosition(pos) {
            this.mouseActive = false
            this.resetMagnification()
            const dock = this.$refs.dock
            if (dock) {
                dock.classList.remove('app-dock--magnifying')
                dock.style.height = ''
                dock.style.width = ''
            }
            this.setPosition(pos)
            this.contextMenu.visible = false
            this.customizeOpen = false
        },
        onClosePanel() {
            if (this.contextMenu.targetItem?.panelId) {
                this.closePanelFn(this.contextMenu.targetItem.panelId)
            }
            this.contextMenu.visible = false
        },
        onToggleWeatherAnimation() {
            weatherService.toggleDockAnimation()
        },
        onToggleMusicAnimation() {
            musicService.toggleDockAnimation()
        },
        onMusicShutdown() {
            musicService.shutdown()
            if (this.contextMenu.targetItem?.panelId) {
                this.closePanelFn(this.contextMenu.targetItem.panelId)
            }
            this.contextMenu.visible = false
        },
        onToggleFmBadge() {
            fileManagerService.toggleDockBadge()
        },
        onToggleAgendaBadge() {
            agendaBadgeService.toggleDockBadge()
        },
        onRemoveFromDock() {
            const {targetItem, targetSection} = this.contextMenu
            if (targetItem && targetSection) {
                this.removeItem(targetSection, targetItem.id)
            }
            this.contextMenu.visible = false
        },

        openCustomizePanel() {
            this.contextMenu.visible = false
            this.customizeOpen = true
        },
        toggleDockItem(section, id) {
            if (this.isItemInDock(section, id)) {
                this.removeItem(section, id)
            } else {
                this.addItem(section, id)
            }
        },

        onWindowResize() {
            this.windowWidth = window.innerWidth
        },
        onKeydown(e) {
            if (e.key === 'Escape' && this.customizeOpen) {
                this.customizeOpen = false
            }
        },
    },
    mounted() {
        window.addEventListener('resize', this.onWindowResize)
        document.addEventListener('keydown', this.onKeydown)
        this._onSwipeStart = (e) => {
            if (!this.dockState.autoHide || this.dockVisible) return
            const touch = e.touches[0]
            if (!touch) return
            const right = this.dockState.position === 'right'
            if (right) {
                if (touch.clientX < window.innerWidth - 50) return
            } else {
                if (touch.clientY < window.innerHeight - 50) return
            }
            const startX = touch.clientX
            const startY = touch.clientY
            const onMove = (ev) => {
                const t = ev.touches[0]
                if (!t) return
                if (right) {
                    if (startX - t.clientX > 20) { this.showDock(); cleanup() }
                } else {
                    if (startY - t.clientY > 20) { this.showDock(); cleanup() }
                }
            }
            const onEnd = () => { cleanup() }
            const cleanup = () => {
                document.removeEventListener('touchmove', onMove)
                document.removeEventListener('touchend', onEnd)
            }
            document.addEventListener('touchmove', onMove)
            document.addEventListener('touchend', onEnd)
        }
        document.addEventListener('touchstart', this._onSwipeStart)
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onWindowResize)
        document.removeEventListener('keydown', this.onKeydown)
        document.removeEventListener('touchstart', this._onSwipeStart)
        clearTimeout(this.hideTimer)
    },
}
</script>
